/**
 * Which reservations Resend, Edit and Release may act on (BON-472).
 *
 * TWO RULES, NOT ONE, and they are asked of different things. Resend and Edit are
 * decided by the STAGE, which is all a re-arm depends on. Release is decided by
 * the SEATS THE ROW IS HOLDING, because that is all a release returns — so a
 * terminal row still holding seats offers it and a live-looking row holding none
 * does not. Collapsing them into one flag gets one of them wrong.
 *
 * The rotation half is the client-side mirror of the server's refusal table
 * (`backend/src/routes/reservation-rotation-responses.ts`). The server is the
 * control — it refuses regardless — but a live-looking button that can only 409
 * is its own defect, and the CSR needs the reason before they click rather than
 * after. Keyed on the DERIVED STAGE because that is all a row has; the server's
 * refusal `code` only exists once a request has been made.
 *
 * BOTH SWITCHES ARE EXHAUSTIVE WITH NO DEFAULT, so a stage added to the DTO is a
 * compile error here rather than a button with an empty tooltip.
 */
import type { ReservationRow, ReservationStage } from '../../api/reservations';

/**
 * Why rotation is refused on this stage, phrased for a tooltip — or `null` when
 * it is allowed. Wording tracks the server's messages for the same states so a
 * CSR reads one explanation, not two.
 */
export function rotationRefusalReason(stage: ReservationStage): string | null {
  switch (stage) {
    case 'link_sent':
    case 'engaged':
    case 'card_declined':
      // Live hold, no money in motion, attempts left. The three payable stages
      // are the whole allowed set.
      return null;
    case 'expired':
      return 'This hold has lapsed and its seats have gone back. Create a new reservation instead.';
    case 'paying':
      // The customer's browser polls its payment status with the SAME
      // credential, so rotating now turns that poll into a 404 mid-verification.
      return 'The customer has a payment in progress. Sending a new link now would cut them off part-way through.';
    case 'attempts_exhausted':
      // No longer "release the seats and…": the strike-out already gave them
      // back (BON-472), so telling a CSR to release first is busy-work that
      // makes the seats look stuck when they are not.
      return 'The customer ran out of payment attempts and the seats have gone back. Create a new reservation instead.';
    case 'released':
      return 'These seats have already been given back. Create a new reservation instead.';
    case 'needs_review':
      return 'This reservation is being looked at by someone. Leave it alone until they are done.';
  }
}

/**
 * Why the seats cannot be handed back on this row, phrased for a tooltip — or
 * `null` when Release is offered.
 *
 * KEYED ON WHAT THE ROW IS HOLDING, not on its stage (BON-472). Release returns
 * `seatsHeld` seats and nothing else, so on a row holding none it returned zero
 * while demanding a required free-text reason and writing an audit entry about
 * seats that never moved. The stage cannot answer this on its own in EITHER
 * direction: a struck-out reservation whose best-effort seat release failed is
 * terminal and still holding, and Release is the only way those seats come back
 * short of a database edit.
 *
 * ONE REASON PER STATE, which is the point: a single "a payment is in progress"
 * string covered three different situations, and on a reservation waiting for a
 * person to review it that sentence sends the CSR off to wait for a payment that
 * does not exist.
 */
export function releaseRefusalReason({
  stage,
  seatsHeld,
}: Pick<ReservationRow, 'stage' | 'seatsHeld'>): string | null {
  const byStage = releaseStageRefusal(stage);
  if (byStage) return byStage;
  // Only once the stage has nothing to say, so a `paying` row is told about its
  // payment rather than about its seat count. Same ordering discipline as the
  // server's refusal table, where the status is the more specific fact.
  return seatsHeld > 0 ? null : NOTHING_TO_RELEASE;
}

const NOTHING_TO_RELEASE =
  'This reservation is not holding any seats, so there is nothing to release.';

/** The refusal the stage alone decides, or null when only the row's held seats
 *  can. Exhaustive with no default, so a new stage is a compile error here
 *  rather than a Release button nobody thought about. */
function releaseStageRefusal(stage: ReservationStage): string | null {
  switch (stage) {
    case 'link_sent':
    case 'engaged':
    case 'card_declined':
    case 'expired':
    case 'attempts_exhausted':
      // Including the two terminal stages. Being finished is not what stops a
      // release — holding nothing is, and that is asked separately above.
      return null;
    case 'paying':
      return 'A payment is in progress. These seats cannot be released until it resolves.';
    case 'needs_review':
      return 'This reservation is being looked at by someone. Leave the seats alone until they are done.';
    case 'released':
      return 'These seats have already been given back. There is nothing left to release.';
  }
}
